import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  PhoneNumber,
  formatIncompletePhoneNumber,
  isValidPhoneNumber,
  isPossiblePhoneNumber,
  NationalNumber,
  E164Number
} from 'libphonenumber-js/max';
import { PhoneNumberModel } from '@utmost.co/enterprise-models';
import { OptionTypeWithExtraMenuItemLabel } from '@utmost.co/utmost-design-system';
import { InternationalPhoneNumberValue } from '../../../../atoms/InternationalPhoneNumber/InternationalPhoneNumberAtom';
import {
  PhoneNumberDetails,
  InternationalPhoneNumberString,
  CountryCode,
  CountryCallingCode,
  InternationalPhoneNumberModel
} from './phoneNumberTypes';
import { DEFAULT_CALLING_CODE, DEFAULT_COUNTRY_CODE } from './phoneNumberConst';
import { ISO_COUNTRIES } from './ISO_COUNTRIES';

const EMPTY_STRING = `+${DEFAULT_CALLING_CODE}`;

export class InternationalPhoneNumber {
  private readonly model: InternationalPhoneNumberModel | undefined = undefined;

  private readonly rawNumber: InternationalPhoneNumberString | undefined = undefined;

  constructor(
    phoneNumber:
      | InternationalPhoneNumberString
      | InternationalPhoneNumberModel
      | Partial<PhoneNumberModel>
      | undefined
      | null
  ) {
    if (InternationalPhoneNumber.isInternationalPhoneNumberString(phoneNumber)) {
      this.rawNumber = phoneNumber || `+${DEFAULT_CALLING_CODE}`;
      this.model = InternationalPhoneNumber.stringToModel(phoneNumber);
    } else if (phoneNumber) {
      this.rawNumber = InternationalPhoneNumber.modelToInternationalRawFormat(phoneNumber);
      this.model = {
        countryCode: phoneNumber.countryCode ?? DEFAULT_CALLING_CODE,
        areaCode: phoneNumber.areaCode ?? '',
        number: phoneNumber.number ?? ''
      };
    }
  }

  isValidModel(): boolean {
    if (!this.model) {
      return true;
    }
    return this.getDetails().isValid;
  }

  isPossibleModel(): boolean {
    if (!this.model) {
      return true;
    }
    return this.getDetails().isPossible;
  }

  getCountryCode(): CountryCode {
    if (!this.model || (this.model.countryCode === '1' && !this.model.number)) {
      return DEFAULT_COUNTRY_CODE;
    }
    const details = this.getDetails();
    if (details.isPossible && details.country) {
      return details.country;
    }
    const item = menuItems.find((item) => item.label === `+${this.model?.countryCode ?? ''}`);
    return item?.value ?? DEFAULT_COUNTRY_CODE;
  }

  toPrettyFormat(): string {
    return this.getDetails().internationalFormat.number ?? '';
  }

  toPrettyNationalFormat(): string {
    return this.getDetails().internationalFormat.nationalNumber ?? '';
  }

  getDetails(): PhoneNumberDetails {
    const numberString: string = this.rawNumber ?? EMPTY_STRING;
    const details: PhoneNumber | undefined = parsePhoneNumberFromString(numberString);
    if (!details) {
      const internationalFormat = InternationalPhoneNumber.stringToInternationalDetails(numberString);
      return {
        isPossible: false,
        isValid: false,
        isEmpty: !internationalFormat.nationalNumber,
        nationalNumber: internationalFormat.nationalNumber as NationalNumber,
        number: internationalFormat.nationalNumber as E164Number,
        internationalFormat,
        model: InternationalPhoneNumber.stringToModel(numberString)
      };
    }

    const { number, areaCode } = InternationalPhoneNumber.getAreaCode(details);
    const { country, countryCallingCode, carrierCode, ext, nationalNumber } = details;
    const type = details?.getType();
    return {
      country,
      countryCallingCode,
      carrierCode,
      ext,
      nationalNumber,
      areaCode: areaCode ?? '',
      number: number as E164Number,
      type,
      isEmpty: false,
      isValid: isValidString(this.rawNumber),
      isPossible: isPossibleString(this.rawNumber),
      internationalFormat: InternationalPhoneNumber.stringToInternationalDetails(this.rawNumber),
      model: {
        areaCode: areaCode ?? '',
        number: number ?? this.rawNumber,
        countryCode: countryCallingCode as string
      }
    };
  }

  static stringToModel(phoneNumberString: InternationalPhoneNumberString): InternationalPhoneNumberModel {
    const internationalFormat = InternationalPhoneNumber.stringToInternationalDetails(phoneNumberString);
    return {
      areaCode: '',
      number: internationalFormat.nationalNumber,
      countryCode: internationalFormat.callingCode as string
    };
  }

  static toInternationalPhoneNumberValue(model?: Partial<PhoneNumberModel>): InternationalPhoneNumberValue {
    return {
      model: model
        ? {
            number: model.number ?? '',
            areaCode: model.areaCode ?? '',
            e164Number: model.e164Number ?? '',
            countryCode: model.countryCode
              ? model.countryCode
              : model.number && model.e164Number
                ? ''
                : DEFAULT_CALLING_CODE
          }
        : undefined
    };
  }

  static mergeToPhoneNumberModel(
    model?: InternationalPhoneNumberModel,
    phoneNumberModel?: Partial<PhoneNumberModel>
  ): Partial<PhoneNumberModel> {
    return {
      ...phoneNumberModel,
      areaCode: model?.areaCode ?? '',
      number: model?.number ?? '',
      countryCode: model?.countryCode ?? DEFAULT_CALLING_CODE
    };
  }

  static getCallingCode(isoCountryCode: CountryCode): CountryCallingCode {
    return getCountryCallingCode(isoCountryCode) ?? DEFAULT_CALLING_CODE;
  }

  static modelToInternationalRawFormat(
    model: InternationalPhoneNumberModel | Partial<PhoneNumberModel>
  ): InternationalPhoneNumberString {
    const newModel = { ...model } as Partial<PhoneNumberModel>;
    if (!model.countryCode && model.number && newModel.e164Number) {
      return newModel.e164Number;
    }
    return `+${model.countryCode ?? DEFAULT_CALLING_CODE}${model.areaCode ?? ''}${model.number ?? ''}`;
  }

  static getMenuItems() {
    return menuItems;
  }

  private static stringToInternationalDetails(
    internationalNumber: InternationalPhoneNumberString = ''
  ): PhoneNumberDetails['internationalFormat'] {
    const internationalFormat = formatIncompletePhoneNumber(internationalNumber);
    const withoutCountryCode: string[] = internationalFormat ? internationalFormat.split(' ') : [];
    const callingCode = withoutCountryCode.shift();
    return {
      number: formatIncompletePhoneNumber(internationalNumber),
      callingCode: (callingCode ? callingCode.replace('+', '') : DEFAULT_CALLING_CODE) as CountryCallingCode,
      nationalNumber: withoutCountryCode.join(' ')
    };
  }

  private static getAreaCode(details: PhoneNumber): { number: string; areaCode: string } {
    if (
      InternationalPhoneNumber.supportsAreaCode(details?.country, details?.countryCallingCode) &&
      details.nationalNumber.length > 4
    ) {
      return {
        areaCode: details.nationalNumber.replace(/\D/g, '').substr(0, 3),
        number: details.nationalNumber.substr(3)
      };
    }
    return {
      areaCode: '',
      number: details.nationalNumber as string
    };
  }

  private static supportsAreaCode(country?: CountryCode, code?: CountryCallingCode): boolean {
    return country === 'US' || country === 'CA' || code === '1';
  }

  private static isInternationalPhoneNumberString(
    phoneNumber: InternationalPhoneNumberString | object | null | undefined
  ): phoneNumber is InternationalPhoneNumberString {
    return typeof phoneNumber === 'string';
  }
}

const regionNamesInEnglish = (Intl as any).DisplayNames
  ? new (Intl as any).DisplayNames(['en'], { type: 'region' })
  : {
      of(code: keyof typeof ISO_COUNTRIES) {
        return ISO_COUNTRIES[code] ?? code;
      }
    };

const menuItems: OptionTypeWithExtraMenuItemLabel<CountryCode>[] = getCountries().map((code) => ({
  label: `+${getCountryCallingCode(code) ?? ''}`,
  menuItemLabel: regionNamesInEnglish.of(code),
  code,
  value: code
}));

function isValidString(internationalNumber: InternationalPhoneNumberString = ''): boolean {
  if (!internationalNumber) {
    return true;
  }
  try {
    return isValidPhoneNumber(internationalNumber);
  } catch (e) {
    return false;
  }
}

function isPossibleString(internationalNumber: InternationalPhoneNumberString = ''): boolean {
  if (!internationalNumber) {
    return true;
  }
  try {
    return isPossiblePhoneNumber(internationalNumber);
  } catch (e) {
    return false;
  }
}
